<!--
Organism for displaying unresulted appointments and non-appt sessions in a grid.
-->
<template>
	<div v-if="firstLoad">{{ t('general.loading') }}</div>
	<div
		v-else
		class="grid h-auto auto-rows-max grid-cols-[minmax(min-content,2fr)_minmax(min-content,1fr)_minmax(min-content,2fr)_minmax(min-content,1fr)_max-content] gap-x-3 rounded-xl border border-indigo-50 bg-white"
		v-auto-animate
	>
		<header
			class="col-span-5 mb-3 grid grid-cols-subgrid border-b border-gray-100 px-5 py-4 font-sans text-base font-bold text-zinc-900"
		>
			<div class="mx-3">{{ t('upcomingAppointments.lead') }}</div>
			<div>{{ t('upcomingAppointments.time') }}</div>
			<div>{{ t('upcomingAppointments.fhName') }}</div>
			<div>{{ t('upcomingAppointments.leadSource') }}</div>
			<BaseToggle
				class="justify-self-end *:text-slate-400"
				v-model="showResulted"
				:label="t('upcomingAppointments.resulted')"
			/>
		</header>
		<template v-if="appointmentDays.length">
			<template
				v-for="day in appointmentDays"
				:key="day.date"
			>
				<div
					class="col-span-5 mx-5 items-center justify-start gap-2.5 rounded-sm bg-gray-100 px-3 py-3"
					data-test-id="appointment-day"
				>
					<div
						class="rounded-sm text-xs font-bold tracking-wide text-gray-500 uppercase"
					>
						{{ day.dateLabel }}
					</div>
				</div>
				<div
					v-auto-animate
					class="col-span-5 grid grid-cols-subgrid divide-y divide-gray-100 px-5"
				>
					<template
						v-for="appt in day.appointments"
						:key="appt.id"
					>
						<NewLeadItem
							v-if="isSession(appt)"
							:session="appt"
							class="hover:bg-background-25 col-span-5 grid grid-cols-subgrid items-start py-4"
						/>
						<AppointmentItem
							v-else
							class="hover:bg-background-25 col-span-5 grid grid-cols-subgrid items-start py-4"
							:appointment="appt"
						/>
					</template>
				</div>
			</template>
		</template>
		<p
			v-else
			class="text-secondary-400 col-span-5 flex items-center justify-center px-8 py-4 text-center text-sm"
		>
			{{
				showResulted
					? t('upcomingAppointments.noResultedAppointments')
					: t('upcomingAppointments.noAppointments')
			}}
		</p>
		<div
			id="print-home-dashboard"
			class="hidden"
		></div>
	</div>
</template>

<script setup lang="ts">
import BaseToggle from '../common/BaseToggle.vue'
import NewLeadItem from './NewLeadItem.vue'
import type { CounselorAppointment } from '@/apiTypes'
import type { Session } from '@/classes/Session'
import AppointmentItem from '@/components/home/AppointmentItem.vue'
import { AppointmentRegardingType } from '@/enums/AppointmentRegardingType'
import { ObjectState } from '@/enums/ObjectState'
import { isSession } from '@/guards'
import { useCrmStore } from '@/stores/crm'
import { useSessionStore } from '@/stores/session'
import { useSyncStore } from '@/stores/sync'
import { assert } from '@vueuse/core'
import { DateTime } from 'luxon'
import { storeToRefs } from 'pinia'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

const { d, t } = useI18n()

interface AppointmentDay {
	date: string
	dateLabel: string
	appointments: Array<CounselorAppointment | Session>
}

const crm = useCrmStore()
const sessionStore = useSessionStore()
const syncStore = useSyncStore()

const showResulted = ref(false)
const { firstLoad } = storeToRefs(syncStore)

/** List of objects with a date label and a heterogeneous list of appointments and sessions */
const appointmentDays = computed<Array<AppointmentDay>>(() => {
	const yesterday = DateTime.now().minus({ days: 1 }).toISODate()
	const today = DateTime.now().toISODate()
	const tomorrow = DateTime.now().plus({ days: 1 }).toISODate()

	const appointments = crm.appointments?.data || []
	const sessions = sessionStore.sessions.filter(
		(s) => s.appointmentId == null && s.salesforceEventAppointmentId == null
	)

	const leads = [...appointments, ...sessions].filter((appt) => {
		if (!appt.startAt) return false

		const apptDate = DateTime.fromISO(appt.startAt).toISODate()
		if (!apptDate) return false

		// Check if this is a resulted appointment
		const isResulted =
			'resultId' in appt ? appt.resultId != null : !!appt.resultedAt

		// Check if this is a canceled appointment
		const isCanceled =
			'objectStateId' in appt && appt.objectStateId === ObjectState.Canceled

		// Check if this is an ACEP appointments
		const isAcep =
			'regardingTypeId' in appt &&
			appt.regardingTypeId === AppointmentRegardingType.ACEP_OR_TCEP

		// Check if this is a Discovery appointment
		const isDiscovery =
			'regardingTypeId' in appt &&
			appt.regardingTypeId === AppointmentRegardingType.DISCOVERY

		// Hide ACEP and Discovery appointments before today
		if ((isAcep || isDiscovery) && apptDate < today) {
			return false
		}

		// If we are showing resulted - show all appointments with a result, and canceled appointments for today only
		// If we are showing unresulted - show all unresulted appts/sessions and canceled appointments for today or later
		// TODO: PSS-1597 will address updating logic for cancelled appointments
		const resultedList = isResulted || (isCanceled && apptDate === today)
		const mainList =
			(!isResulted && !isCanceled) || (isCanceled && apptDate >= today)
		return showResulted.value ? resultedList : mainList
	})

	// Group appointments by day
	const days = leads.reduce((days: Array<AppointmentDay>, appt) => {
		if (appt.startAt === null) return days

		const apptStart = DateTime.fromISO(appt.startAt)
		let dayMatch = days.find((day) => day.date === apptStart.toISODate())

		// Create a new day if it doesn't exist
		if (!dayMatch) {
			dayMatch = {
				date: apptStart.toISODate() || '',
				dateLabel: (() => {
					switch (apptStart.toISODate()) {
						case yesterday:
							return t('upcomingAppointments.yesterday')
						case today:
							return t('upcomingAppointments.today')
						case tomorrow:
							return t('upcomingAppointments.tomorrow')
						default:
							return d(apptStart.toJSDate(), 'mediumDate')
					}
				})(),
				appointments: [],
			}

			days.push(dayMatch)
		}

		assert(dayMatch !== undefined)

		// Add the appointment to the day
		dayMatch!.appointments.push(appt)

		return days
	}, [])

	// Make sure the days are in order
	days.sort((a, b) => {
		const diff = DateTime.fromISO(a.date)
			.diff(DateTime.fromISO(b.date))
			.as('days')
		return showResulted.value ? -diff : diff
	})

	// Make sure all the appointments/sessions for each day are in order
	days.forEach((day) => {
		day.appointments.sort((a, b) => {
			if (a.startAt === null) return 1
			if (b.startAt === null) return -1

			return DateTime.fromISO(a.startAt)
				.diff(DateTime.fromISO(b.startAt))
				.as('minutes')
		})
	})

	return days
})

/** Expose appointmentDays for unit tests */
defineExpose({
	appointmentDays,
})
</script>
